'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { useAuthUser } from './useAuthUser';

const STORAGE_KEY = 'lexomat_dev_mode';
const DEV_ALLOWLIST_EMAILS: string[] = (
  process.env['NEXT_PUBLIC_DEV_ALLOWLIST_EMAILS'] ?? ''
)
  .split(',')
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

const readStoredDevMode = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return null;
};

/**
 * Returns true if dev mode is active.
 *
 * Priority:
 * 1) Explicit local toggle (localStorage) if set.
 * 2) Allowlist/email, ?dev=true, or NODE_ENV === 'development'.
 *
 * Used to gate dev-only features (orgs, Google Drive, etc.).
 *
 * SSR-safe two-pass pattern (issue #3718 sub-3): initial storedOverride
 * is `null` so the SSR pass uses the same fallback path as the first
 * client render — no hydration mismatch warning, no SSR/CSR flash for
 * users who have flipped the toggle. The useEffect-mounted listener
 * reads localStorage on first commit and updates state if a stored
 * override exists.
 */
export function useDevMode(): boolean {
  const { userEmail } = useAuthUser();
  const searchParams = useSearchParams();
  const [storedOverride, setStoredOverride] = useState<boolean | null>(null);

  useEffect(() => {
    // First pass: read the persisted override once after mount.
    setStoredOverride(readStoredDevMode());

    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setStoredOverride(readStoredDevMode());
      }
    };
    const handleCustom = () => {
      setStoredOverride(readStoredDevMode());
    };
    window.addEventListener('storage', handleStorage);
    window.addEventListener('devmode:toggle', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('devmode:toggle', handleCustom);
    };
  }, []);

  const baseDev = useMemo(() => {
    const isDev = process.env.NODE_ENV === 'development';
    const isAllowlisted =
      !!userEmail && DEV_ALLOWLIST_EMAILS.includes(userEmail.toLowerCase());
    const hasDevParam = searchParams.get('dev') === 'true';
    return isDev || isAllowlisted || hasDevParam;
  }, [searchParams, userEmail]);

  return storedOverride ?? baseDev;
}

export function toggleDevMode(next?: boolean) {
  if (typeof window === 'undefined') return;
  const current = readStoredDevMode();
  const resolvedNext = typeof next === 'boolean' ? next : !(current ?? false);
  localStorage.setItem(STORAGE_KEY, String(resolvedNext));
  window.dispatchEvent(new Event('devmode:toggle'));
}
